/**
 * Loom's story model, shared by the Worker and the client. Pure functions only (docs/ideas/loom.md → M1 in detail).
 *
 * A story is one Self-Graph doc in the player's UserMind: the world graph, the running recap and scene, and the last
 * TAIL turns. Older turns are logged as `loom.turn` events once they are final (canon never changes after that).
 */
import type { Dice } from "../dice";
import type { LedgerFact } from "./ledger";
import type { Meanwhile } from "./offscreen";

/** A move can be a word or a scene: up to about 650 words (owner: "what if someone writes 4 paragraphs?"). */
export const INPUT_MAX_CHARS = 4000;
export const OPENING_MAX_CHARS = 4000;
/** Turns kept in the story doc (the narrator sees the last 3; the client shows these on reopen). */
export const TAIL = 30;
/** A chapter: the narrator's cached prompt restarts from a fresh snapshot every this many turns. */
export const CHAPTER_TURNS = 8;
/** World entities the narrator sees (the most recent, plus every open thread). */
export const WORLD_EXCERPT = 40;
/** Recap is rewritten every this many turns (by the canon pass). */
export const RECAP_EVERY = 6;
export const STORIES_PER_DAY = 3;
export const TURNS_PER_DAY = 150;
export const LOOKS_PER_DAY = 200;

export type Intent = "action" | "dialogue" | "thought" | "meta";
export type EntityKind = "place" | "person" | "thing" | "thread";

export interface Entity {
  /** Canonical name ("the keeper" → "keeper"), unique per story. */
  id: string;
  kind: EntityKind;
  name: string;
  /** One short line of what is true about it. */
  hint: string;
  /** Id of the entity it was found inside ("look closer" children). */
  parent?: string;
  /** Threads only: an open question or promise the story owes the player. */
  state?: "open" | "closed";
  /** Turn it first appeared in. */
  turn: number;
  /** Set once "look closer" has expanded it (its children are in the world). */
  looked?: boolean;
  /** Turns the canon pass saw it in (newest last, at most SEEN_MAX): threads fade and return by these. */
  seen?: number[];
  /** Threads only: the pressure it puts on the story (docs/ideas/loom-tension.md). */
  t?: Tension;
  /** A setup: the thread (id) this thing was planted for, and the turn a reveal paid it off. */
  sets?: string;
  paid?: number;
  /** Places only: the story made it unreachable as it was (the train has left); going back there clears it. */
  gone?: boolean;
  /** Things only: the player has it on them (the canon pass's "carry" and "drop" notes): "On you". */
  carried?: boolean;
}

/**
 * The scene's soundscape, from a fixed list (the canon pass picks one per turn; the client plays it). Each key is a
 * slot: today a sound synthesized in the browser, later a recorded loop or a generated piece.
 */
/**
 * V2, the page is the world (docs/ideas/loom-v2.md, phase 1): the scene's weather, light and crowd, as the canon pass reads
 * them from the reply. The page turns them into type: rain slants the newest lines, fog fades what came before, a crowd
 * packs the text tight.
 */
export const WEATHERS = ["clear", "rain", "storm", "snow", "fog", "wind", "heat"] as const;
export type Weather = (typeof WEATHERS)[number];
export const LIGHTS = ["dawn", "day", "dusk", "night", "dark", "lamplight"] as const;
export type Light = (typeof LIGHTS)[number];
/** Crowd: 0 alone, 1 a few people, 2 busy, 3 packed. */
export interface Sky { weather: Weather; light: Light; crowd: 0 | 1 | 2 | 3 }

/**
 * V2, living margins: small true things beside a turn (the margin pass, worker/turn.ts marginPass). Something overheard,
 * words written on a wall, something happening out of sight, a stranger passing. `follow` is a move the player can make
 * with one tap; `ties` names the thread or person it quietly bears on (never shown: the lab and the narrator use it).
 */
export const ASIDE_KINDS = ["overheard", "on the wall", "elsewhere", "a stranger"] as const;
export type AsideKind = (typeof ASIDE_KINDS)[number];
export interface Aside { kind: AsideKind; text: string; follow?: string; ties?: string }

export const AMBIENCES = ["silence", "rain", "wind", "sea", "train", "city", "cafe", "jazz", "fire", "night", "house", "clock", "crowd", "water"] as const;
export type Ambience = (typeof AMBIENCES)[number];
/** The score's tags, chosen by the canon pass for each scene: which instruments, and how it feels. */
export const ENSEMBLES = [
  "silence", "felt piano", "solo cello", "solo violin", "string quartet", "acoustic guitar", "guitar and bass", "woodwinds", "harp",
  "bells", "mallets", "choir", "music box", "brass", "electric piano",
] as const;
export type Ensemble = (typeof ENSEMBLES)[number];
export const FEELS = ["tender", "serene", "wonder", "melancholy", "mysterious", "playful", "romantic", "tense", "ominous", "urgent", "triumphant", "eerie"] as const;
export type Feel = (typeof FEELS)[number];
export interface MusicTags { ensemble: Ensemble; feel: Feel }
/**
 * The recorded loop library (Stable Audio 3; packages/lab/loom/sound-library.mjs): SOUND_TAKES 64 s loops per soundscape
 * in R2 at sound/loom/<key>-<n>.mp3, shared by every player. SOUND_REV is in the URL so a regenerated library is never
 * served from an old cache: bump it whenever the files change.
 */
export const SOUND_TAKES = 3;
export const SOUND_REV = 1;
/** The recorded-instrument set the client loads (R2 sound/loom/samples/<version>/, built by packages/lab/loom/samples). */
export const SAMPLES_VERSION = "v1";
export const SOUND_FILE_RE = new RegExp(`^(${AMBIENCES.filter((a) => a !== "silence").join("|")})-[0-${SOUND_TAKES - 1}]\\.mp3$`);
export const soundUrl = (key: Exclude<Ambience, "silence">, take: number) => `/api/sound/${SOUND_REV}/${key}-${take}.mp3`;

/** What kind of pressure a thread is. */
export type Form = "question" | "secret" | "threat" | "clock" | "promise" | "desire";
export const FORMS = ["question", "secret", "threat", "clock", "promise", "desire"] as const;
export interface Tension {
  form: Form;
  /** 0–5: how hard it presses now. */
  heat: number;
  /** What changes in the world if it goes badly (a few words). */
  stakes: string;
  /** Trouble coming, a tick at a time; `on` is what happens when it fills. */
  clock?: { filled: number; size: 4 | 6 | 8; on: string };
  by: "player" | "world";
  /** How it ended, when a chapter resolved on it. */
  outcome?: Outcome;
}

/** The director's state (shared/drama.ts). */
export type Phase = "calm" | "build" | "peak" | "fade";
export type BeatKind = "quiet" | "plant" | "escalate" | "shock" | "reveal" | "turn" | "complicate" | "reprieve" | "confront" | "resolve";
export type Temperament = "cassandra" | "phoebe" | "randy";
/** A prepared beat (a storylet): an event in the story's own terms that the director can use when its kind comes up. */
export interface Card { id: string; kind: BeatKind; thread: string; text: string; pays?: string; by: "canon" | "dream" | "premise"; turn: number }
export type Outcome = "earned" | "turned" | "lost";
export interface Chapter {
  n: number; from: number; question: string; stakes: string; thread: string;
  /** What the player has secured that bears on the question (the canon pass keeps it). */
  leverage?: string[];
  /** How it ended, and when: earned (the player's move won it), turned (answered at a price), lost (the moment passed). */
  outcome?: Outcome; closedAt?: number;
  /** The question's shape (src/dice.ts SHAPES), so the next chapter asks in another. */
  shape?: string;
  /** The story overtook the question once (already done, or meaningless now) and the canon pass asked a fresh one. */
  replaced?: true;
}
export interface Drama {
  temperament: Temperament;
  phase: Phase;
  /** Turn the phase began, and how many turns it is meant to last. */
  phaseAt: number;
  phaseLen: number;
  /** Turn of the last sudden event. */
  lastSpike: number;
  /** Structural pressure at which a build becomes a peak; Cassandra raises it a little at each peak. */
  peakTarget: number;
  /** The last beats, newest last (at most 20). */
  beats: { n: number; kind: BeatKind; spike: boolean; resist?: true }[];
  /** No sudden events before this turn (after the player saw crisis resources and said they're okay). */
  calmUntil?: number;
  /** Turn of the last time the world pushed back (a resist beat). */
  lastResist?: number;
  /** The chapter's crisis: when it was reached, and how many turns the player has let pass without acting on it. */
  crisis?: { at: number; stall: number };
  /** Turns in a row the player lowered the stakes: taken as a wish for longer calms, not punished. */
  lowerStreak?: number;
  /** The chapter's dramatic question, and the closed ones. */
  chapter?: Chapter;
  chapters?: Chapter[];
  /** Prepared beats, newest last (at most 8). */
  cards?: Card[];
  /** The story bible's events (worker/premise.ts), in order: real happenings with people in them, used one at a time. */
  deck?: Card[];
  /** Turn of the last deck event used, and of the inciting event (it happens once, by the fifth turn). */
  deckAt?: number;
  incited?: number;
  /** The story's spine: the question across all its chapters, and how many chapters it runs. */
  spine?: { question: string; chapters: number };
  /** The player asked for an ending: the current or next chapter is the finale (its resolution ends the story). */
  finale?: { at: number };
  /** The turns the player last asked for trouble and played a flashback (each once a chapter). */
  trouble?: number;
  flashback?: number;
  /** V2: the turns the player wove a thread (/weave) that held; a refused one is taken off again. */
  woven?: number[];
  /** The story ended (its finale resolved); afterwards it is an epilogue the player may linger in. */
  ended?: { at: number; outcome: Outcome };
}

export const SEEN_MAX = 40;

export interface Turn {
  n: number;
  input: string;
  /** Canon prose: what the player saw. Ends in "—" when the player cut in. */
  prose: string;
  /** streaming: the narrator is still writing (or the write hasn't landed); done: whole reply; cut: player cut in. */
  status: "streaming" | "done" | "cut";
  /** Words of the reply the player cut away before seeing them ("41 words unwoven"). */
  unwoven?: number;
  /** A dormant thread the dream planned came back in this turn (its name). */
  callback?: string;
  /** A thread the player pulled (tapped) to bring back in this turn (its name). */
  pulled?: string;
  /** The director's beat for this turn, and whether it was a sudden event (logs, lab). */
  beat?: BeatKind;
  spike?: boolean;
  /** What Jev read in the player's move (logs, lab tuning). */
  read?: { stakes?: string; engages?: string; confronts?: boolean; fit?: number };
  /** A chapter ended in this turn (the client shows its card). */
  chapterEnd?: ChapterEnd;
  /** A command that shaped this turn (/ask with its answer, /elsewhere). */
  verb?: Verb;
  /** Facts the player's line made true, as the canon pass read them (the page marks the line as kept). */
  kept?: string[];
  /** The planned return that fired in this turn (the canon pass puts it back if the reply left it out). */
  firedCallback?: Callback;
  /** The whole story ended in this turn (its spine answered). */
  storyEnd?: { question: string; outcome: Outcome };
  /** The thread the director's beat was about (the canon pass ties a planted thing to it). */
  beatOn?: string;
  /** The beat used a prepared card (lab). */
  card?: boolean;
  /** The narrator's voice turned with the story at this turn (the page shows a line above the reply). */
  voiceShift?: { from: string; to: string };
  intent: Intent | null;
  tension: number;
  at: number;
  /** The canon pass (world, scene, recap) has read this turn. A cut turn is read once its cut is known. */
  canon?: boolean;
  /** When a canon pass took this turn to read (ms): no other starts on it for CANON_LEASE_MS (claimCanon). */
  reading?: number;
  /** Where the player was at the end of this turn (a place's id), as the canon pass read it: places "you've been" and
   *  "you're here" come from these, never from mere mentions. */
  here?: string;
  /** The scene's soundscape after this turn (the canon pass's pick): a place's mark on the map comes from these. */
  ambience?: Ambience;
  /** The scene's weather, light and crowd after this turn (V2). */
  sky?: Sky;
  /** What drifted into the margins beside this turn (V2). */
  asides?: Aside[];
  /** V2, a thread woven (/weave): what the world wove back ("the world pulls back"), or that it refused the weave. */
  weave?: { pull?: string; refused?: true };
}

export interface StoryDoc {
  v: 1;
  id: string;
  title: string;
  /** Free flow: the player turned chapters off for this story (no chapter questions, crises or chapter endings). */
  free?: boolean;
  createdAt: number;
  updatedAt: number;
  /** Number of the latest turn (turns are numbered from 1). */
  n: number;
  /** Jev's latest tension reading, 1–5 (3 before the first reading). */
  tension: number;
  /** A few sentences on where the player is and what is going on: the state Jev reads. */
  scene: string;
  /** "Previously on…": rewritten every RECAP_EVERY turns. */
  recap: string;
  recapAt: number;
  world: Entity[];
  turns: Turn[];
  /** Set when a crisis check flagged the player's words; the story waits for "I'm okay". */
  crisis: boolean;
  /** User-scope memo keys of this story's "look closer" expansions (deleted with the story). */
  looks: string[];
  /** The current chapter's starting point (the narrator's cached prompt prefix). */
  snap?: Snapshot;
  /** The player's IANA time zone (the nightly dream runs in their night). */
  tz?: string;
  /** What the last dream planned: dormant threads to bring back when the moment fits (Jev decides when). */
  plan?: { at: number; callbacks: Callback[] };
  /** What the player's own lines made true (about themselves or the scene), newest last (at most TRUTHS_MAX). */
  truths?: Truth[];
  /** People who left the player's company (sent away, gone home) and haven't come back: the canon pass's "away" and
   *  "back" notes. The narrator leaves them out of the cast until the player seeks them out (player feedback, 2026-10-29:
   *  a dismissed band kept playing). At most AWAY_MAX, newest last. */
  away?: { name: string; turn: number }[];
  /** The story ledger (src/shared/ledger.ts): what the story has settled about its people and things, kept by the canon
   *  pass every turn and rebuilt by a batch pass every LEDGER_EVERY turns and at the dream (`ledgerAt`: the last turn a
   *  rebuild read). */
  ledger?: LedgerFact[];
  ledgerAt?: number;
  /** What the latest reply got wrong against the ledger (the canon pass's "slip" note): the next reply lets the world
   *  account for it, in the story, without retelling it. */
  slip?: { text: string; n: number };
  /** The narrator's voice for this story (src/voices.ts), fixed when it begins; absent (stories from before voices): Classic. */
  voice?: string;
  /** A reply to a morning letter, waiting in the line as the player's next move (cleared when a turn is taken). */
  waiting?: { text: string; at: number };
  /** What the player asked of the storyteller ("slower please", "no spiders"), newest last (at most HOUSE_MAX). */
  house?: Truth[];
  /** Chapter images the dreams made (private R2 keys, served only to this player). */
  images?: StoryImage[];
  /** Things the dream expects the player to examine next ("look closer" is pre-written for these, at batch price). */
  prefetch?: string[];
  /** The last dream: when, and up to which turn it read. */
  dreamt?: { at: number; n: number };
  /** What the player is afraid of, from the opening truths: it shadows the opening and seeds the first chapter's stakes. */
  fear?: string;
  /** Pass it on: the seed this story was begun from, and the one its player passed on from it (src/worker/seeds.ts). */
  seedFrom?: string;
  seedGiven?: string;
  /** The turn the story ended on, once its last plate was asked for (asked once per story). */
  lastPlate?: number;
  /** The story map's painted ground (one per dream, the newest kept): private R2, under the drawn map. */
  ground?: { key: string; at: number; drawnAt: number };
  /** The tension director (shared/drama.ts). */
  drama?: Drama;
  /** The player chose this story's voice (in the story map, or for all new stories): it never turns with the story. */
  voiceLocked?: true;
  /** The voice turning with the story (worker/voice-turn.ts): the last reading, and a turn due at the next chapter. */
  voiceRead?: { voice: string; p: number; at: number };
  voiceNext?: string;
  /** The last time the voice turned with the story: at which turn, and from which voice. */
  voiceTurned?: { n: number; from: string };
  /** The story's dice and its hidden bones (worker/premise.ts): never shown to the player. */
  premise?: Premise;
  /** The real town or region the story is in, as the canon pass last named it (worker/place.ts), and since when. */
  setting?: { place: string; at: number };
  /** The current scene's soundscape. */
  ambience?: Ambience;
  /** The scene's music tags (the canon pass's choice). */
  music?: MusicTags;
  /** The scene's weather, light and crowd now (V2; absent until the canon pass has read one). */
  sky?: Sky;
  /** V2, words as things: names the player pocketed from a look closer, to write with later (at most POCKET_MAX, newest
   *  last). What they carry ("On you") is in the pocket too, from the world graph, not from here. */
  pocket?: Pocketed[];
  /** V2, the world moves while you're away (shared/offscreen.ts): what the last dream says happened out of the player's
   *  sight, waiting to be shown and to become canon with their next move (`applied`: the turn that took it). */
  offscreen?: Meanwhile;
}

export interface Truth { text: string; turn: number; /** Made true with the Thread pen (/weave). */ woven?: true }

/** Someone in the story bible's cast: what they want, how they talk, and what they hide (shown in deeds, never said). */
export interface CastMember { name: string; role: string; wants: string; voice: string; hides: string }
/**
 * A story's premise: the dice rolled when it began (src/dice.ts), and the story bible written once after its first reply
 * (the cast, the inciting event, a first chapter question, and the opening's motifs for the player's memory).
 */
export interface Premise {
  dice: Dice;
  cast?: CastMember[];
  inciting?: string;
  question?: string;
  motifs?: string[];
  /** Worn-out moves the first reply used: the narrator and the canon pass let them pass. */
  worn?: string[];
  /** When the bible was written (absent until then). */
  at?: number;
}

/**
 * Commands that shape the world (co-creation step 2): a line starting "/ask" asks the world a question (an oracle,
 * answered inside the fiction); "/elsewhere" frames a new scene somewhere else or later. The line is kept as typed;
 * "/journal" (or "/map") never reaches the server (the page opens the journal).
 */
export type Verb = { kind: "ask"; answer: Oracle } | { kind: "elsewhere" } | { kind: "trouble" } | { kind: "clock"; until: string } | { kind: "flashback"; text: string } | { kind: "weave"; text: string };
export type Oracle = "yes" | "yes-but" | "no" | "no-and";
export type VerbKind = "ask" | "elsewhere" | "trouble" | "clock" | "flashback" | "weave";
export function verbOf(input: string): { kind: VerbKind; text: string } | null {
  const m = /^\/(ask|elsewhere|trouble|clock|flashback|weave)\b\s*(.*)$/is.exec(input.trim());
  if (!m) return null;
  const kind = m[1]!.toLowerCase() as VerbKind, text = m[2]!.trim();
  // /trouble needs nothing after it (the world picks); the others need words.
  return text || kind === "trouble" ? { kind, text } : null;
}

/** Player clocks open at once, at most. */
export const PLAYER_CLOCKS_MAX = 2;
/** Turns between two /trouble (or two /flashback) in a story without chapters (with chapters: once a chapter). */
export const COMMAND_GAP = 10;
/**
 * Why a command can't be used now (or null): /trouble and /flashback once a chapter (every COMMAND_GAP turns without
 * chapters); at most PLAYER_CLOCKS_MAX of the player's own clocks running. Checked before a turn is charged.
 */
export function commandRefused(doc: Pick<StoryDoc, "drama" | "world" | "n">, kind: VerbKind): "trouble_used" | "flashback_used" | "clocks_full" | "weaves_used" | null {
  if (kind === "weave") return weavesLeft(doc).left > 0 ? null : "weaves_used";
  if (kind === "clock") return doc.world.filter((e) => e.state !== "closed" && e.t?.by === "player" && e.t.clock).length >= PLAYER_CLOCKS_MAX ? "clocks_full" : null;
  if (kind !== "trouble" && kind !== "flashback") return null;
  const last = doc.drama?.[kind];
  if (last === undefined) return null;
  const ch = doc.drama?.chapter && !doc.drama.chapter.outcome ? doc.drama.chapter : undefined;
  const used = ch ? last >= ch.from : doc.n + 1 - last < COMMAND_GAP;
  return used ? (kind === "trouble" ? "trouble_used" : "flashback_used") : null;
}
/**
 * V2, two pens (docs/ideas/loom-v2.md, phase 3): Ink is what you do; Thread (/weave) is what you make true. Three
 * threads a chapter; between chapters, and in free flow, one every COMMAND_GAP turns. A weave the world refused as
 * impossible gives its thread back (the canon pass's "refused" note). Checked on the server before a turn is charged.
 */
export const WEAVES_PER_CHAPTER = 3;
export function weavesLeft(doc: Pick<StoryDoc, "drama" | "n">): { left: number; of: number } {
  const woven = doc.drama?.woven ?? [];
  const ch = doc.drama?.chapter && !doc.drama.chapter.outcome ? doc.drama.chapter : undefined;
  if (ch) return { left: Math.max(0, WEAVES_PER_CHAPTER - woven.filter((n) => n >= ch.from).length), of: WEAVES_PER_CHAPTER };
  return { left: woven.some((n) => doc.n + 1 - n < COMMAND_GAP) ? 0 : 1, of: 1 };
}
/** A woven truth holds for the rest of its chapter (COMMAND_GAP turns without one): the canon pass can't retire it. */
export function wovenHolds(t: Truth, doc: Pick<StoryDoc, "drama">, n: number): boolean {
  if (!t.woven) return false;
  const ch = doc.drama?.chapter && !doc.drama.chapter.outcome ? doc.drama.chapter : undefined;
  return ch ? t.turn >= ch.from : n - t.turn < COMMAND_GAP;
}
/** The oracle's answer, from a seeded roll: the world leans to complications while the pressure is up. */
export function oracleOf(r: () => number, pressing: boolean): Oracle {
  const w: [Oracle, number][] = pressing ? [["yes", 0.25], ["yes-but", 0.3], ["no", 0.2], ["no-and", 0.25]] : [["yes", 0.35], ["yes-but", 0.3], ["no", 0.2], ["no-and", 0.15]];
  let x = r();
  for (const [k, p] of w) if ((x -= p) < 0) return k;
  return "no-and";
}
export const TRUTHS_MAX = 30, HOUSE_MAX = 6, AWAY_MAX = 8;

/**
 * V2, words as things (docs/ideas/loom-v2.md, phase 4): the pocket. What the player carries ("On you", the canon pass's
 * carry and drop notes) is in it already; a look closer adds "Pocket it", which keeps a name to write with later (a tap
 * on it puts the name in the line, with either pen). A name keeps no power of its own: the story still decides what a
 * line can do. What the story has since taken away shows as such and can't be written with: a thing now with someone
 * else (the ledger's "has"), one the ledger says was destroyed or lost, or a place made unreachable.
 */
export const POCKET_MAX = 12;
export interface Pocketed {
  /** The thing's entity id when it was pocketed. */ id: string; name: string; turn: number;
  /** Its hint then: a later hint that newly says it's gone or with someone else counts (the canon pass rewrites a thing's
   *  hint more reliably than it writes a drop or has note: 0 of 9 in the lab, 2026-10-30). */
  hint?: string;
}
export interface PocketItem {
  id: string; name: string; hint: string;
  /** "you": carried (On you); "word": a name pocketed from a look closer. */
  on: "you" | "word";
  /** away: with someone else now; gone: destroyed, lost or out of reach. Either way it can't be written with. */
  state?: "away" | "gone";
  /** Why, in the story's words ("Pavel has the brass key"). */
  note?: string;
}
/** Words that say a thing is no more, or no longer anyone's to use. */
const UNDONE = /\b(destroy(?:ed|s)?|broken|smash(?:ed|es)?|shatter(?:ed|s)?|burn(?:ed|t|s)|melt(?:ed|s)|lost|stolen|sank|sunk|drown(?:ed|s)|dead|died|killed|eaten|used up|torn up|ruined|confiscated|no longer exists?|taken from (?:you|the player))\b/i;
/** The words of a name worth matching on ("the brass key" → brass, key). */
const nameWords = (name: string) => entityId(name).split(/[^\p{L}\p{N}]+/u).filter((w) => w.length >= 3);
const plain = (text: string) => ` ${entityId(text).replace(/[^\p{L}\p{N}]+/gu, " ")} `;
export const mentions = (text: string, name: string) => {
  const t = plain(text);
  const words = nameWords(name);
  return words.length > 0 && words.every((w) => t.includes(` ${w}`));
};
/** The fact says the thing is no more, in words other than its name ("the lost key was found" isn't). */
const undone = (text: string, name: string) => UNDONE.test(nameWords(name).reduce((t, w) => t.replaceAll(` ${w} `, " "), plain(text)));
/** A hint that puts the thing with someone else ("now in the midwife's hand", "Pavel keeps it", "handed to the manager"). */
const WITH_SOMEONE = /\b(?:now (?:in|with)\b|in (?:\p{Lu}[\p{L}'’-]*|the [\p{L}'’-]+)['’]s? (?:hands?|keeping|pocket|bag|coat|possession)|handed (?:it |over )?to|given to|taken by|belongs to \p{Lu}|(?:\p{Lu}[\p{L}'’-]*|the [\p{L}'’-]+) (?:has|holds|keeps|took) it\b)/u;

/** The pocket as the player sees it: what they carry first, then the names they pocketed, newest first. Pure. */
export function pocketOf(doc: Pick<StoryDoc, "world" | "pocket" | "ledger">): PocketItem[] {
  const ledger = doc.ledger ?? [];
  const byId = new Map(doc.world.map((e) => [e.id, e]));
  const carried = doc.world.filter((e) => e.kind !== "thread" && e.carried)
    .map((e): PocketItem => ({ id: e.id, name: e.name, hint: e.hint, on: "you" }));
  const onYou = new Set(carried.map((c) => c.id));
  const words = [...(doc.pocket ?? [])].reverse().flatMap((p): PocketItem[] => {
    // A dream may have folded the entity into another under a new id: follow it by name.
    const e = byId.get(p.id) ?? byId.get(entityId(p.name));
    const name = e?.name ?? p.name;
    const item: PocketItem = { id: e?.id ?? p.id, name, hint: e?.hint ?? "", on: "word" };
    // Its hint since it was pocketed: only a change counts (a vase broken from the start isn't "gone"). This also
    // overrules a carry the canon pass never dropped (the vase shattered in the player's hands).
    if (e && p.hint !== undefined && e.hint !== p.hint) {
      if (undone(e.hint, name) && !undone(p.hint, name)) return [{ ...item, state: "gone", note: e.hint }];
      if (WITH_SOMEONE.test(e.hint) && !WITH_SOMEONE.test(p.hint)) return [{ ...item, state: "away", note: e.hint }];
    }
    if (e && onYou.has(e.id)) return [];
    if (e?.kind === "place" && e.gone) return [{ ...item, state: "gone", note: `${name.charAt(0).toUpperCase()}${name.slice(1)} is out of reach now.` }];
    // The ledger, newest first: someone else has it, or it is no more.
    for (const f of [...ledger].reverse()) {
      if (f.k === "has" && mentions(f.about, name)) return [{ ...item, state: "away", note: f.text }];
      if ((f.k === "world" || f.k === "role") && mentions(f.text, name) && undone(f.text, name)) return [{ ...item, state: "gone", note: f.text }];
    }
    return [item];
  });
  // A carried thing its pocketed name shows taken away is no longer on the player.
  const overruled = new Set(words.filter((w) => w.state).map((w) => w.id));
  return [...carried.filter((c) => !overruled.has(c.id)), ...words];
}

/** The pocket after putting a thing in or taking it out: "full" at POCKET_MAX, "unknown" for a name not in the story. */
export function pocketAfter(doc: Pick<StoryDoc, "world" | "pocket" | "n">, thing: string, put: "in" | "out"): Pocketed[] | "full" | "unknown" {
  const id = entityId(thing);
  const was = doc.pocket ?? [];
  if (put === "out") return was.filter((p) => p.id !== id && entityId(p.name) !== id);
  const e = doc.world.find((x) => x.id === id);
  if (!e || e.kind === "thread") return "unknown";
  if (was.some((p) => p.id === id)) return was;
  if (was.length >= POCKET_MAX) return "full";
  return [...was, { id, name: e.name, turn: doc.n, hint: e.hint }];
}
/** Who is away after this exchange: those who came back leave the list, those who left join it (one entry a name). */
/** The cast after the canon pass's "role" notes: a member the story changed (exposed, changed sides) is what they are now. */
export function castAfter(cast: readonly CastMember[], roles: readonly { name: string; role: string }[]): CastMember[] {
  return cast.map((c) => { const r = roles.find((x) => entityId(x.name) === entityId(c.name)); return r ? { ...c, role: r.role } : c; });
}
export function awayAfter(was: readonly { name: string; turn: number }[], notes: { away: readonly string[]; back: readonly string[] }, n: number): { name: string; turn: number }[] {
  const back = new Set(notes.back.map(entityId));
  const left = notes.away.filter((x) => !back.has(entityId(x)));
  const gone = new Set(left.map(entityId));
  return [...was.filter((x) => !back.has(entityId(x.name)) && !gone.has(entityId(x.name))), ...left.map((name) => ({ name, turn: n }))]
    .filter((x, k, all) => all.findIndex((y) => entityId(y.name) === entityId(x.name)) === k).slice(-AWAY_MAX);
}
/** A truth set before the story began (the opening's "true here"), or a law of the world the opening line laid down:
 *  the story's ground. Only the player can strike one; the canon pass never retires it. */
export const isGround = (t: Truth) => t.turn === 0;
/** The truths as the narrator reads them: the ground first, as (ground-truths …), then the rest as (truths …). */
export function truthsSexp(ts: readonly Truth[]): string {
  const list = (xs: readonly Truth[]) => xs.map((t) => JSON.stringify(t.text)).join(" ");
  const ground = ts.filter(isGround), rest = ts.filter((t) => !isGround(t));
  return [ground.length ? `(ground-truths ${list(ground)})` : "", rest.length ? `(truths ${list(rest)})` : ""].filter(Boolean).join("\n");
}
export interface Callback {
  thread: string; name: string; how: string; cue: string;
  /** Times it fired and the reply left it out (it is put back at most RETURN_TRIES times). */
  tries?: number;
}
/** A chapter's end, as its card shows it: what the player had (leverage) and, unless earned, the price paid. */
export interface ChapterEnd { n: number; question: string; outcome: Outcome; suggestEnd?: boolean; price?: string; leverage?: string[] }
export interface StoryImage {
  at: number; key: string; caption: string;
  /** The plate's code (shared/plates.ts plateCode) and when it was drawn; minted with the image, or on first view. */
  code?: string; drawnAt?: number;
  /** Which of the plate styles it was drawn in (prompts.ts IMAGE_STYLES); absent on images from before the mix. */
  style?: "haze" | "planes";
  /** The last plate: drawn the moment the story ended, shown on its end card. */
  last?: boolean;
}

/** Frozen when a chapter opens: what happened before (recap + the last turns verbatim), the scene and the world. */
export interface Snapshot { from: number; recap: string; scene: string; world: string; before?: string; truths?: string; place?: string }

/**
 * The suggested openings: three at random on each visit (shuffleOpenings), every first reply written fresh for its
 * story. The night train with tomorrow's ticket is gone: every story was starting there (owner, 2026-10-19).
 */
export const OPENINGS = [
  "The lighthouse keeper has been expecting me for eleven years.",
  "At the night market, a woman is selling my own coat back to me.",
  "The laundromat is open all night, and tonight every machine is running.",
  "My grandmother's house has one more room than it did last summer.",
  "The ferry left without me, but my suitcase is on it.",
  "I've been hired to water the plants in an empty office tower.",
  "The snow started in July, and only on our street.",
  "Someone has been leaving fresh bread on my doorstep for a week.",
  "I'm the last guest at a wedding, and the band won't stop playing.",
  "The museum asked me to catalogue a box nobody remembers donating.",
  "My new apartment came with a cat that knows the way to somewhere else.",
  "The swimming pool was drained years ago, but tonight it's full.",
] as const;
/** `count` of the suggested openings, in random order (a new draw on each visit). */
export function shuffleOpenings(count = 3, rand: () => number = Math.random): string[] {
  const a: string[] = [...OPENINGS];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); [a[i], a[j]] = [a[j]!, a[i]!]; }
  return a.slice(0, count);
}

export interface StorySummary { id: string; title: string; updatedAt: number; n: number }
export interface StoryIndex {
  stories: StorySummary[];
  /** After the player saw crisis resources and said they're okay, only the explicit-phrase pre-filter runs until then. */
  crisisQuietUntil?: number;
}

/** What the client gets for a story (never the raw world internals it doesn't need). */
export interface StoryView {
  id: string;
  title: string;
  n: number;
  tension: number;
  recap: string;
  turns: (Pick<Turn, "n" | "input" | "prose" | "status" | "intent" | "tension" | "unwoven" | "callback" | "pulled" | "spike" | "chapterEnd" | "storyEnd" | "voiceShift">
    & { /** The chapter's moment of decision arrived in this turn. */ decision?: true; kept?: string[]; verb?: VerbKind; here?: string; ambience?: Ambience; sky?: Sky; asides?: Pick<Aside, "kind" | "text" | "follow">[]; weave?: Turn["weave"] })[];
  /** What the player's lines made true, and their house rules (the journal's "Kept"). */
  truths: string[];
  house: string[];
  /** A reply to a morning letter, to put in the line. */
  waiting: string | null;
  /** Turns where a chapter began (the cloth view draws a band there). */
  chapterStarts: number[];
  /** The open chapter's question (shown above the line), and whether its moment of decision has come. */
  chapter: { n: number; question: string; crisis: boolean } | null;
  /** The player asked for an ending (and it hasn't come yet); the story has ended. */
  finale: boolean;
  /** Free flow: no chapters in this story (the player's choice). */
  free: boolean;
  /** The story's narrator voice (src/voices.ts); "classic" for stories from before voices. */
  voice: string;
  /** The voice turned with the story (not chosen by the player): when, and from which voice (the story map offers it back). */
  voiceSince: { n: number; from: string } | null;
  ended: boolean;
  /** The current scene's soundscape (played when the player turns sound on). */
  ambience: Ambience;
  /** The scene's music: instruments and feel (null until the canon pass has chosen). */
  music: MusicTags | null;
  /** The scene's weather, light and crowd (V2; null until the canon pass has read one): the page takes it on. */
  sky: Sky | null;
  /** V2, two pens: the Thread pen's threads left now, of how many (three a chapter; one at a time without one). */
  weave: { left: number; of: number };
  /** V2, words as things: what is in the player's pocket (pocketOf): what they carry, then the names they pocketed. */
  pocket: PocketItem[];
  /** Places, people and things: tappable names ("look closer"), the map (parents) and the found objects. */
  things: ThingView[];
  /** Where the player is now (a place's id), and every place they have been, first visit first (ids). Empty for stories
   *  from before the canon pass read where the player was. */
  here: string | null;
  been: string[];
  /** The story's open questions (and closed ones), with the turns they were seen in. */
  threads: ThreadView[];
  crisis: boolean;
  /** Chapter images: shown after turn `at` (the last turn the dream read); fetched from /api/stories/:id/images/:i. */
  images: { i: number; at: number; caption: string; code: string | null; drawnAt: number | null; last?: true }[];
  /** When the story began and ended (its first turn, the turn that ended it): the colophon's dates. */
  begun: number | null;
  endedAt: number | null;
  /** Pass it on: this story was begun from someone's seed, or its opening was passed on (either: endings to compare). */
  seed: { from: boolean; given: boolean } | null;
  /** When the player last played it (its latest turn): "Previously…" when they come back after a while. */
  lastAt: number | null;
  /** The last plate has been asked for (it may still be drawing). */
  lastPlate: boolean;
  /** The map's painted ground, when a dream has painted one (fetched from /api/stories/:id/ground?v=drawnAt). */
  ground: { at: number; drawnAt: number } | null;
  /** How many quiet threads the last dream planned to bring back (a count only: which ones is the surprise). */
  returning: number;
  /** When the loom last dreamt this story (null: never). */
  dreamt: { at: number; n: number } | null;
  /** V2: what happened while the player was away, still waiting for their next move ("While you were away"; null:
   *  nothing waiting). `at`: the dream that wrote it. */
  offscreen: { at: number; hours: number; lines: string[] } | null;
}
export interface ThingView { id: string; kind: Exclude<EntityKind, "thread">; name: string; hint: string; parent?: string; looked?: boolean; turn: number; seen: number[]; gone?: true; carried?: true }
export interface ThreadView { id: string; name: string; hint: string; state: "open" | "closed"; turn: number; seen: number[] }

/** NDJSON lines a turn streams. */
export type StreamLine =
  | { t: "turn"; n: number; intent: Intent | null; tension: number; story: string; callback?: string }
  | { t: "prose"; text: string }
  | { t: "end" }
  | { t: "crisis"; resources: Resource[] }
  | { t: "error"; error: string };

export interface Cut { turn: number; chars: number }
export interface Resource { name: string; detail: string; href: string; alt?: { label: string; href: string } }
export interface LookView { thing: string; description: string; children: Pick<Entity, "id" | "kind" | "name" | "hint">[] }

export const storyDocName = (id: string) => `loom:story:${id}`;
export const INDEX_DOC = "loom:index";
/** The player's settings: mature themes (18+, confirmed), and whether new stories have chapters (default: yes). */
export const SETTINGS_DOC = "loom:settings";
export interface Settings {
  mature: boolean; confirmedAt?: number; chapters?: boolean;
  /** The narrator voice for new stories: a voice id, or absent / "auto" to pick one from each story's opening. */
  voice?: string;
  /** Let a long story's voice turn at a new chapter, as the player or the story change (default on). */
  voiceFollows?: boolean;
  /** How plates draw the player; absent: from the story (a woman if it shows one, otherwise a man). */
  drawAs?: DrawAs;
}
export type DrawAs = "man" | "woman";
/** The Account choice: "story" (the default) leaves it to the story. */
export type DrawAsChoice = DrawAs | "story";
export const DOC_PREFIX = "loom:";

/** "  The Keeper " → "keeper" (same rule as the kernel's canonical(), duplicated so the client stays kernel-free). */
export function entityId(name: string): string {
  return name.normalize("NFKC").toLowerCase().replace(/\s+/g, " ").trim()
    .replace(/^[\s"'“”‘’.,;:!?()-]+|[\s"'“”‘’.,;:!?()-]+$/gu, "")
    .replace(/^(a|an|the)\s+/, "");
}

/**
 * The canon of an interrupted reply: never longer than what was generated nor than what the player saw, snapped
 * back to the last whole word, ending in "—". A cut at (or past) the end keeps the whole reply.
 */
export function cutProse(generated: string, shown: number): { prose: string; cut: boolean } {
  const chars = Math.max(0, Math.min(Math.floor(shown), generated.length));
  if (chars >= generated.trimEnd().length) return { prose: generated.trimEnd(), cut: false };
  let head = generated.slice(0, chars);
  // Mid-word (the next character continues it): drop the partial word. "One|." keeps "One".
  if (/^[\p{L}\p{N}'’]/u.test(generated.slice(chars)) && /[\p{L}\p{N}'’]$/u.test(head)) head = head.replace(/\S+$/, "");
  head = head.replace(/[\s,;:—–-]+$/u, "");
  return { prose: head ? `${head}—` : "—", cut: true };
}

/** Adds new entities and updates known ones (same canonical name); threads can be closed. Pure. */
export function mergeWorld(
  world: readonly Entity[],
  changes: { entities: { kind: EntityKind; name: string; hint: string; parent?: string }[]; closed?: string[]; touched?: string[] },
  turn: number,
): Entity[] {
  const byId = new Map(world.map((e) => [e.id, { ...e }]));
  for (const c of changes.entities) {
    const id = entityId(c.name);
    if (!id) continue;
    const known = byId.get(id);
    if (known) {
      if (c.hint) known.hint = c.hint;
      // A thread re-mentioned reopens, unless a chapter ended on it: that ending stands.
      if (known.kind === "thread" && c.kind === "thread" && !known.t?.outcome) known.state = "open";
      known.seen = seenAt(known.seen, turn);
    } else {
      const parent = c.parent ? entityId(c.parent) : undefined;
      byId.set(id, {
        id, kind: c.kind, name: c.name.trim(), hint: c.hint.trim(), turn, seen: [turn],
        ...(parent && byId.has(parent) ? { parent } : {}),
        ...(c.kind === "thread" ? { state: "open" as const } : {}),
      });
    }
  }
  for (const name of changes.touched ?? []) {
    const e = byId.get(entityId(name));
    if (e) e.seen = seenAt(e.seen, turn);
  }
  for (const name of changes.closed ?? []) {
    const e = byId.get(entityId(name));
    if (e?.kind === "thread") { e.state = "closed"; e.seen = seenAt(e.seen, turn); }
  }
  return [...byId.values()];
}

const seenAt = (seen: number[] | undefined, turn: number) => [...(seen ?? []).filter((n) => n !== turn), turn].slice(-SEEN_MAX);

/**
 * Folds near-duplicates into one entity (the dream finds them: "knitting that trails away" = "knitting that runs under
 * seats"). The kept one gets every turn the others were seen in, their children, and stays open if any was open.
 * Pairs of different kinds (a thread and a thing) are never merged. Pure.
 */
export function mergeDuplicates(world: readonly Entity[], merges: readonly { keep: string; drop: readonly string[] }[]): Entity[] {
  return foldDuplicates(world, merges).world;
}
/** mergeDuplicates, and which id each dropped entity went into (turns that name a dropped place follow it). Pure. */
export function foldDuplicates(world: readonly Entity[], merges: readonly { keep: string; drop: readonly string[] }[]): { world: Entity[]; into: Map<string, string> } {
  const byId = new Map(world.map((e) => [e.id, { ...e }]));
  const into = new Map<string, string>();
  for (const m of merges) {
    const keep = byId.get(entityId(m.keep));
    if (!keep) continue;
    for (const name of m.drop) {
      const id = entityId(name);
      const d = byId.get(id);
      if (!d || d.id === keep.id || into.has(id) || (d.kind === "thread") !== (keep.kind === "thread")) continue;
      keep.seen = [...new Set([...(keep.seen ?? [keep.turn]), ...(d.seen ?? [d.turn])])].sort((a, b) => a - b).slice(-SEEN_MAX);
      keep.turn = Math.min(keep.turn, d.turn);
      if (keep.kind === "thread" && (keep.state === "open" || d.state === "open")) keep.state = "open";
      if (d.looked) keep.looked = true;
      if (d.carried) keep.carried = true;
      into.set(id, keep.id);
      byId.delete(id);
    }
  }
  return { world: [...byId.values()].map((e) => (e.parent && into.has(e.parent) ? { ...e, parent: into.get(e.parent)! } : e)), into };
}

/**
 * How long a canon pass holds the turn it reads (Turn.reading). Longer than production lets background work run after
 * a reply (30 s), so a pass that was cut off there is started again by the next turn, the leave-page beacon or a later
 * one, while one that is still running is never doubled.
 */
export const CANON_LEASE_MS = 60_000;

/** Can a canon pass start on turn n at `now`: not read yet, has something to read, and no other pass holds it. Pure. */
export function canonDue(t: Turn | undefined, now: number): t is Turn {
  return !!t && !t.canon && t.status !== "streaming" && t.intent !== "meta" && !!t.prose.trim()
    && !(t.reading !== undefined && now - t.reading < CANON_LEASE_MS);
}

/** Turn n taken for a canon pass at `now`, or null when it isn't due (canonDue). Pure. */
export function claimCanon(doc: StoryDoc, n: number, now: number): StoryDoc | null {
  if (!canonDue(doc.turns.find((t) => t.n === n), now)) return null;
  return { ...doc, turns: doc.turns.map((t) => (t.n === n ? { ...t, reading: now } : t)) };
}

/** A failed pass lets go of its turn at once (only its own claim), so the next trigger tries again. Pure. */
export function releaseCanon(doc: StoryDoc, n: number, at: number): StoryDoc | null {
  const t = doc.turns.find((x) => x.n === n);
  if (!t || t.canon || t.reading !== at) return null;
  return { ...doc, turns: doc.turns.map((x) => (x.n === n ? (({ reading: _r, ...rest }) => rest)(x) : x)) };
}

/** The last few turns still waiting for a canon pass that one could start now, oldest first. Pure. */
export function unreadTurns(doc: StoryDoc, now: number, back = 3): number[] {
  return doc.turns.slice(-back).filter((t) => canonDue(t, now)).map((t) => t.n);
}

/** Turns whose place was folded into another follow it. Pure; the same array when nothing moved. */
export function followFolds(turns: Turn[], into: ReadonlyMap<string, string>): Turn[] {
  if (!into.size || !turns.some((t) => t.here && into.has(t.here))) return turns;
  return turns.map((t) => (t.here && into.has(t.here) ? { ...t, here: into.get(t.here)! } : t));
}

/**
 * The place the canon pass named as where the player is, if the world knows it as a place (its id). The name may be
 * shorter or longer than the world's ("the platform" for "nameless platform"): then the known place whose words hold
 * all of its words (or the other way round), the one seen most recently.
 */
export function placeId(world: readonly Entity[], name: string | null | undefined): string | undefined {
  const id = name ? entityId(name) : "";
  if (!id) return undefined;
  const places = world.filter((e) => e.kind === "place");
  if (places.some((e) => e.id === id)) return id;
  const words = (s: string) => new Set(s.split(/[^\p{L}\p{N}'’]+/u).filter((w) => w.length > 2));
  const mine = words(id);
  const within = (a: Set<string>, b: Set<string>) => a.size > 0 && [...a].every((w) => b.has(w));
  const last = (e: Entity) => Math.max(e.turn, ...(e.seen ?? []));
  return places.filter((e) => { const theirs = words(e.id); return within(mine, theirs) || within(theirs, mine); })
    .sort((a, b) => last(b) - last(a))[0]?.id;
}

/** A thing's hint that says the player has it on them now ("a lantern you carry now", "folded into your coat"). */
const HELD = /\b(?:you (?:now )?(?:carry|carried|hold|held|keep|kept|took|take|clutch|grip)|you(?:'ve| have) (?:taken|got|picked)|(?:in|into) your (?:hands?|pocket|coat|jacket|bag|arms|palm|fist|satchel|waistcoat)|on your (?:belt|back|person|shoulder)|now yours)\b/i;
/** …and the hints that say it left them, which win ("you set it down on the bench"). */
const LET_GO = /\b(?:set (?:it )?down|(?:now )?(?:sits|rests|lies|stands) (?:on|in|by|at)|put (?:it )?(?:back|down)|left (?:it|behind|on|at|in)|leave it|no longer|hand(?:ed|s)? (?:it )?(?:to|over)|gave (?:it )?to|given to|dropped|lost|broken|shattered|smashed|burn(?:ed|t))\b/i;
/**
 * Things the canon pass's own hints put on the player, where it wrote no "carry" note: it rewrites a thing's hint ("a
 * railway lantern you have taken from its hook and now carry") far more reliably than it writes the note (taken along:
 * 1 of 3 noted in the lab, 2026-10-30). Only hints this reading wrote. English hints only; the note still covers the
 * rest. Pure.
 */
export function heldByHint(entities: readonly { kind: string; name: string; hint: string }[]): string[] {
  return entities.filter((e) => e.kind === "thing" && HELD.test(e.hint) && !LET_GO.test(e.hint)).map((e) => e.name);
}

/** The thing the canon pass named (its id): the exact name, else the known thing whose words hold all of its words (or the other way round), the latest. */
export function thingId(world: readonly Entity[], name: string | null | undefined): string | undefined {
  const id = name ? entityId(name) : "";
  if (!id) return undefined;
  const things = world.filter((e) => e.kind === "thing");
  if (things.some((e) => e.id === id)) return id;
  const words = (s: string) => new Set(s.split(/[^\p{L}\p{N}'’]+/u).filter((w) => w.length > 2 && !["your", "my", "the", "late", "old"].includes(w)));
  const mine = words(id);
  const within = (a: Set<string>, b: Set<string>) => a.size > 0 && [...a].every((w) => b.has(w));
  return things.filter((e) => { const theirs = words(e.id); return within(mine, theirs) || within(theirs, mine); }).sort((a, b) => b.turn - a.turn)[0]?.id;
}

/** Where the player is now and every place they have been (first visit first), from the turns. */
export function placesOf(world: readonly Entity[], turns: readonly Pick<Turn, "here">[]): { here: string | null; been: string[] } {
  const places = new Set(world.filter((e) => e.kind === "place").map((e) => e.id));
  const been: string[] = [];
  let here: string | null = null;
  for (const t of turns) {
    if (!t.here || !places.has(t.here)) continue;
    here = t.here;
    if (!been.includes(t.here)) been.push(t.here);
  }
  return { here, been };
}

/**
 * A request to the storyteller as a house rule: the player's words, without what wraps them ("(shorter replies,
 * please)", "“no spiders”", "Storyteller: slower") and with a capital letter. The words themselves are kept.
 */
export function houseRuleOf(input: string): string {
  let t = input.replace(/\s+/g, " ").trim();
  for (let k = 0; k < 3; k++) {
    const before = t;
    t = t.replace(/^[(\[{]\s*([^()[\]{}]*?)\s*[)\]}]$/u, "$1").replace(/^["“]\s*([^"“”]*?)\s*["”]$/u, "$1").replace(/^['‘]\s*([^'‘’]*?)\s*['’]$/u, "$1")
      .replace(/^(?:(?:hey|ok|okay|dear)\s+)?(?:storyteller|narrator|printer|loom)\s*[,:—–-]\s*/iu, "").trim();
    if (t === before) break;
  }
  return (t.charAt(0).toUpperCase() + t.slice(1)).slice(0, 160);
}

/** The part of the world the narrator sees: every open thread plus the most recent entities. */
export function worldExcerpt(world: readonly Entity[], limit = WORLD_EXCERPT): Entity[] {
  const open = world.filter((e) => e.kind === "thread" && e.state === "open");
  const rest = world.filter((e) => !(e.kind === "thread" && e.state === "open")).sort((a, b) => b.turn - a.turn);
  return [...open, ...rest].slice(0, Math.max(limit, open.length));
}

/** One entity as an s-expression, e.g. `(place "flooded lighthouse" :hint "…" :in "coast")`. */
export function entitySexp(e: Entity): string {
  const parts = [`(${e.kind} ${JSON.stringify(e.name)}`, `:hint ${JSON.stringify(e.hint)}`];
  if (e.parent) parts.push(`:in ${JSON.stringify(e.parent)}`);
  if (e.state) parts.push(`:state ${e.state}`);
  if (e.t) {
    parts.push(`:form ${e.t.form} :heat ${e.t.heat}`);
    if (e.t.stakes) parts.push(`:stakes ${JSON.stringify(e.t.stakes)}`);
    if (e.t.clock) parts.push(`:clock ${e.t.clock.filled}/${e.t.clock.size} :when-full ${JSON.stringify(e.t.clock.on)}`);
  }
  return `${parts.join(" ")})`;
}

export function worldSexp(world: readonly Entity[]): string {
  return world.length ? `(world\n  ${world.map(entitySexp).join("\n  ")})` : "(world)";
}

export function viewOf(doc: StoryDoc): StoryView {
  return {
    id: doc.id, title: doc.title, n: doc.n, tension: doc.tension, recap: doc.recap, crisis: doc.crisis, free: !!doc.free, voice: doc.voice ?? "classic",
    truths: (doc.truths ?? []).map((t) => t.text), house: (doc.house ?? []).map((h) => h.text), waiting: doc.waiting?.text ?? null,
    turns: doc.turns.map(({ n, input, prose, status, intent, tension, unwoven, callback, pulled, spike, chapterEnd, storyEnd, beat, kept, verb, here, ambience, sky, asides, weave, voiceShift }) => ({
      n, input, prose, status, intent, tension, ...(unwoven ? { unwoven } : {}), ...(callback ? { callback } : {}), ...(pulled ? { pulled } : {}),
      ...(spike ? { spike } : {}), ...(chapterEnd ? { chapterEnd } : {}), ...(storyEnd ? { storyEnd } : {}), ...(beat === "confront" ? { decision: true as const } : {}),
      ...(kept?.length ? { kept } : {}), ...(verb ? { verb: verb.kind } : {}), ...(here ? { here } : {}), ...(ambience ? { ambience } : {}),
      ...(sky ? { sky } : {}), ...(voiceShift ? { voiceShift } : {}), ...(weave ? { weave } : {}),
      ...(asides?.length ? { asides: asides.map(({ kind, text, follow }) => ({ kind, text, ...(follow ? { follow } : {}) })) } : {}),
    })),
    voiceSince: doc.voiceTurned && !doc.voiceLocked ? doc.voiceTurned : null,
    chapterStarts: [...(doc.drama?.chapters ?? []), ...(doc.drama?.chapter ? [doc.drama.chapter] : [])].map((c) => c.from),
    chapter: doc.drama?.chapter && !doc.drama.chapter.outcome
      ? { n: doc.drama.chapter.n, question: doc.drama.chapter.question, crisis: !!doc.drama.crisis } : null,
    ambience: doc.ambience ?? "silence",
    music: doc.music ?? null,
    sky: doc.sky ?? null,
    weave: weavesLeft(doc),
    pocket: pocketOf(doc),
    ...placesOf(doc.world, doc.turns),
    finale: !!doc.drama?.finale && !doc.drama.ended,
    ended: !!doc.drama?.ended,
    things: doc.world.flatMap((e): ThingView[] => (e.kind === "thread" ? [] : [{
      id: e.id, kind: e.kind, name: e.name, hint: e.hint, turn: e.turn, seen: e.seen ?? [e.turn],
      ...(e.parent ? { parent: e.parent } : {}), ...(e.looked ? { looked: true } : {}), ...(e.gone ? { gone: true as const } : {}), ...(e.carried ? { carried: true as const } : {}),
    }])),
    threads: doc.world.flatMap((e): ThreadView[] => (e.kind !== "thread" ? [] : [{
      id: e.id, name: e.name, hint: e.hint, state: e.state ?? "open", turn: e.turn, seen: e.seen ?? [e.turn],
    }])),
    images: (doc.images ?? []).map(({ at, caption, code, drawnAt, last }, i) => ({ i, at, caption, code: code ?? null, drawnAt: drawnAt ?? null, ...(last ? { last: true as const } : {}) })),
    begun: doc.turns[0]?.at ?? null,
    lastAt: doc.turns.at(-1)?.at ?? null,
    endedAt: doc.turns.find((t) => t.storyEnd)?.at ?? null,
    seed: doc.seedFrom || doc.seedGiven ? { from: !!doc.seedFrom, given: !!doc.seedGiven } : null,
    lastPlate: doc.lastPlate !== undefined,
    ground: doc.ground ? { at: doc.ground.at, drawnAt: doc.ground.drawnAt } : null,
    returning: doc.plan?.callbacks.length ?? 0,
    dreamt: doc.dreamt ?? null,
    offscreen: doc.offscreen && doc.offscreen.applied === undefined && doc.offscreen.events.length
      ? { at: doc.offscreen.at, hours: doc.offscreen.hours, lines: doc.offscreen.events.map((x) => x.text) } : null,
  };
}

/** A short title from the opening line: its first words, up to ~6. */
export function titleOf(opening: string): string {
  const words = opening.replace(/\s+/g, " ").trim().split(" ");
  const t = words.slice(0, 6).join(" ").replace(/[.,;:!?—–-]+$/u, "");
  return words.length > 6 ? `${t}…` : t;
}

/** Tension → the narrator's pacing note. */
export function pacing(tension: number): "breathe" | "hold" | "build" {
  if (tension >= 4.5) return "breathe";
  if (tension <= 2) return "build";
  return "hold";
}
